import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Tag, Modal, Form, Input, Select, DatePicker, message, Space, InputNumber, Popconfirm, List, Typography, Row, Col, Tabs, Grid, Upload, Image } from 'antd';
import { PlusOutlined, MedicineBoxOutlined, EditOutlined, DeleteOutlined, HistoryOutlined, DollarOutlined, UploadOutlined, CameraOutlined } from '@ant-design/icons';
import client from '../api/client';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

const { Option } = Select;
const { Title } = Typography;
const MAX_MORTALITY_PHOTO_SIZE_MB = 5;
const MAX_MORTALITY_PHOTOS = 5;
const ACCEPTED_MORTALITY_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const LivestockPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isStaff = user?.role === 'STAFF';
    const [batches, setBatches] = useState([]);
    const [partners, setPartners] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [livestockItems, setLivestockItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const totalHeads = batches.reduce((sum, b) => sum + Number(b.current_head_count || 0), 0);
    const activeBatches = batches.filter(b => b.status === 'ACTIVE').length;

    const [isBatchModalVisible, setIsBatchModalVisible] = useState(false);
    const [isEventModalVisible, setIsEventModalVisible] = useState(false);
    const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
    const [isSaleModalVisible, setIsSaleModalVisible] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [editingBatch, setEditingBatch] = useState(null);
    const [batchEvents, setBatchEvents] = useState([]);
    const [batchSales, setBatchSales] = useState([]);

    const [batchForm] = Form.useForm();
    const [eventForm] = Form.useForm();
    const [saleForm] = Form.useForm();
    const [caretakerForm] = Form.useForm();
    const [isCaretakerModalVisible, setIsCaretakerModalVisible] = useState(false);
    const [mortalityPhotos, setMortalityPhotos] = useState([]);
    const [editingEvent, setEditingEvent] = useState(null);
    const eventType = Form.useWatch('event_type', eventForm);
    const storageBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
    const isDerivedEventEdit = editingEvent && ['BIRTH', 'STAGE_UPGRADE'].includes(editingEvent.event_type);

    useEffect(() => {
        fetchBatches();
        fetchPartners();
        fetchCustomers();
        fetchBranches();
        fetchLivestockItems();
    }, []);

    const fetchBatches = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/transactions/livestock');
            setBatches(data);
        } catch (error) {
            message.error('Failed to load livestock batches');
        } finally {
            setLoading(false);
        }
    };

    const fetchPartners = async () => {
        try {
            const { data } = await client.get('/master/partners');
            setPartners(data.filter(p => p.type === 'FARMER' || p.type === 'BOTH'));
        } catch (error) {
            console.error('Failed to load partners');
        }
    };

    const fetchCustomers = async () => {
        try {
            const { data } = await client.get('/master/partners');
            setCustomers(data.filter(p => p.type === 'CUSTOMER' || p.type === 'BOTH'));
        } catch (error) {
            console.error('Failed to load customers');
        }
    };

    const fetchBranches = async () => {
        try {
            const { data } = await client.get('/master/branches');
            setBranches(data);
        } catch (error) {
            console.error('Failed to load branches');
        }
    };

    const fetchLivestockItems = async () => {
        try {
            const { data } = await client.get('/master/items');
            const livestockOnly = (data || []).filter(item => {
                const categoryName = item.category?.name?.toLowerCase() || '';
                const categorySlug = item.category?.slug?.toLowerCase() || '';
                return categoryName === 'livestocks' || categorySlug === 'livestocks';
            });
            setLivestockItems(livestockOnly);
        } catch (error) {
            console.error('Failed to load livestock items');
        }
    };

    const handleSubmitBatch = async (values) => {
        try {
            const payload = {
                ...values,
                acquisition_date: values.acquisition_date.format('YYYY-MM-DD'),
            };

            if (editingBatch) {
                await client.put(`/transactions/livestock/${editingBatch.id}`, payload);
                message.success('Livestock batch updated successfully');
            } else {
                await client.post('/transactions/livestock', payload);
                message.success('Livestock batch registered successfully');
            }
            setIsBatchModalVisible(false);
            setEditingBatch(null);
            batchForm.resetFields();
            fetchBatches();
        } catch (error) {
            message.error('Failed to save batch');
        }
    };

    const handleDeleteBatch = async (id) => {
        try {
            await client.delete(`/transactions/livestock/${id}`);
            message.success('Batch deleted successfully');
            fetchBatches();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to delete batch');
        }
    };

    const openBatchModal = (batch = null) => {
        setEditingBatch(batch);
        if (batch) {
            batchForm.setFieldsValue({
                ...batch,
                acquisition_date: dayjs(batch.acquisition_date),
            });
        } else {
            batchForm.resetFields();
            batchForm.setFieldsValue({ acquisition_date: dayjs() });
        }
        setIsBatchModalVisible(true);
    };

    const closeEventModal = () => {
        setIsEventModalVisible(false);
        setEditingEvent(null);
        eventForm.resetFields();
        setMortalityPhotos([]);
    };

    const handleRecordEvent = async (values) => {
        try {
            const formData = new FormData();
            formData.append('event_type', values.event_type);
            formData.append('event_date', values.event_date.format('YYYY-MM-DD'));
            formData.append('head_count_change', values.head_count_change ?? 0);
            if (values.cost_incurred) formData.append('cost_incurred', values.cost_incurred);
            if (values.notes) formData.append('notes', values.notes);
            if (values.new_item_id) formData.append('new_item_id', values.new_item_id);
            if (values.new_unit_cost_per_head) formData.append('new_unit_cost_per_head', values.new_unit_cost_per_head);

            mortalityPhotos.forEach((f) => {
                if (f.originFileObj) {
                    formData.append('photos[]', f.originFileObj);
                } else if (f.existingPath) {
                    formData.append('retained_photo_paths[]', f.existingPath);
                }
            });

            if (editingEvent) {
                await client.post(`/transactions/livestock/events/${editingEvent.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                message.success('Event updated successfully');
            } else {
                await client.post(`/transactions/livestock/${selectedBatch.id}/events`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                message.success('Event recorded successfully');
            }

            closeEventModal();
            fetchBatches();
            if (isHistoryModalVisible && selectedBatch) {
                const { data } = await client.get(`/transactions/livestock/${selectedBatch.id}`);
                setBatchEvents(data.events || []);
                setBatchSales(data.sales || []);
                setSelectedBatch(data);
            }
        } catch (error) {
            const data = error.response?.data;
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
            message.error(data?.message || validationErrors || (editingEvent ? 'Failed to update event' : 'Failed to record event'));
        }
    };

    const openEventModal = (batch) => {
        setSelectedBatch(batch);
        setEditingEvent(null);
        eventForm.resetFields();
        eventForm.setFieldsValue({ event_date: dayjs(), head_count_change: 0 });
        setMortalityPhotos([]);
        setIsEventModalVisible(true);
    };

    const openEditEventModal = (event) => {
        setEditingEvent(event);
        eventForm.resetFields();
        eventForm.setFieldsValue({
            event_type: event.event_type,
            event_date: dayjs(event.event_date),
            head_count_change: event.head_count_change ?? 0,
            cost_incurred: event.cost_incurred ? Number(event.cost_incurred) : undefined,
            notes: event.notes,
            new_item_id: event.new_item_id,
            new_unit_cost_per_head: event.new_unit_cost_per_head,
        });
        setMortalityPhotos((event.photo_paths || []).map((path, index) => ({
            uid: `existing-${index}`,
            name: path.split('/').pop() || `photo-${index + 1}`,
            status: 'done',
            url: `${storageBaseUrl}/storage/${path}`,
            existingPath: path,
        })));
        setIsEventModalVisible(true);
    };

    const openSaleModal = (batch) => {
        setSelectedBatch(batch);
        saleForm.resetFields();
        saleForm.setFieldsValue({
            sale_date: dayjs(),
            head_count_sold: 1,
            branch_id: user?.branch?.id,
        });
        setIsSaleModalVisible(true);
    };

    const handleRecordSale = async (values) => {
        try {
            const { data: sale } = await client.post(`/transactions/livestock/${selectedBatch.id}/sales`, {
                ...values,
                sale_date: values.sale_date.format('YYYY-MM-DD'),
            });
            message.success('Sale recorded successfully');
            setIsSaleModalVisible(false);
            saleForm.resetFields();
            await fetchBatches();

            if (isHistoryModalVisible) {
                setBatchSales((currentSales) => [sale, ...currentSales]);
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to record sale');
        }
    };

    const openCaretakerModal = () => {
        caretakerForm.resetFields();
        setIsCaretakerModalVisible(true);
    };

    const handleCreateCaretaker = async (values) => {
        try {
            const payload = { ...values, type: 'FARMER', is_active: true };
            const { data } = await client.post('/master/partners', payload);
            message.success('Caretaker created successfully');
            setIsCaretakerModalVisible(false);
            fetchPartners();
            if (data?.id) {
                batchForm.setFieldValue('partner_id', data.id);
            }
        } catch (error) {
            const data = error.response?.data;
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
            message.error(data?.message || validationErrors || 'Failed to create caretaker');
        }
    };

    const viewHistory = async (batch) => {
        setSelectedBatch(batch);
        setLoading(true);
        try {
            const { data } = await client.get(`/transactions/livestock/${batch.id}`);
            setBatchEvents(data.events || []);
            setBatchSales(data.sales || []);
            setSelectedBatch(data);
            setIsHistoryModalVisible(true);
        } catch (error) {
            message.error('Failed to load history');
        } finally {
            setLoading(false);
        }
    };

    const batchColumns = [
        {
            title: 'Batch / Livestock',
            key: 'batch',
            render: (_, record) => {
                const originalName = record.item?.name || record.species || 'Livestock';
                const currentStage = record.current_stage_item?.name;
                const parentBatch = record.parent_batch?.batch_number;
                return (
                    <div>
                        <div style={{ fontWeight: 'bold' }}>
                            {currentStage ? currentStage : originalName}
                            {record.breed && ` - ${record.breed}`}
                        </div>
                        {currentStage && (
                            <Tag color="orange" style={{ fontSize: 10, marginTop: 2 }}>
                                Started as: {originalName}
                            </Tag>
                        )}
                        {parentBatch && (
                            <Tag color="green" style={{ fontSize: 10, marginTop: 2 }}>
                                Born from: {parentBatch}
                            </Tag>
                        )}
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{record.batch_number}</Typography.Text>
                    </div>
                );
            }
        },
        { title: 'Caretaker', dataIndex: ['caretaker', 'name'], key: 'caretaker' },
        {
            title: 'Head Count',
            dataIndex: 'current_head_count',
            key: 'current_head_count',
            render: (val) => (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 'bold' }}>{val}</div>
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>HEADS</Typography.Text>
                </div>
            )
        },
        { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => <Tag color={status === 'ACTIVE' ? 'green' : 'orange'} style={{ borderRadius: 12, padding: '0 12px' }}>{status}</Tag> },
        { title: 'Acquired', dataIndex: 'acquisition_date', key: 'acquisition_date', render: (date) => dayjs(date).format('MMM D, YYYY') },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<MedicineBoxOutlined />} onClick={() => openEventModal(record)}>
                        Event
                    </Button>
                    <Button size="small" icon={<HistoryOutlined />} onClick={() => viewHistory(record)}>
                        History
                    </Button>
                    {record.status === 'ACTIVE' && Number(record.current_head_count) > 0 && (
                        <Button size="small" type="primary" icon={<DollarOutlined />} onClick={() => openSaleModal(record)}>
                            Sale
                        </Button>
                    )}
                    <Button size="small" icon={<EditOutlined />} onClick={() => openBatchModal(record)} />
                    {!isStaff && (
                        <Popconfirm title="Delete?" onConfirm={() => handleDeleteBatch(record.id)}>
                            <Button size="small" icon={<DeleteOutlined />} danger />
                        </Popconfirm>
                    )}
                </Space>
            )
        },
    ];

    const StatCard = ({ title, value, prefix, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {prefix}{value.toLocaleString()}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Livestock Hub</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage livestock batches, health events, and caretaker assignments.</Typography.Text>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={() => openBatchModal(null)}
                    style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px', width: isMobile ? '100%' : 'auto' }}
                >
                    Register New Batch
                </Button>
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Total Head Count"
                        value={totalHeads}
                        icon={<i className="fa-solid fa-hippo"></i>}
                        color="#ef4444"
                        subText="Population in all batches"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Active Batches"
                        value={activeBatches}
                        icon={<i className="fa-solid fa-folder-tree"></i>}
                        color="#3b82f6"
                        subText="Ongoing programs"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Active Partners"
                        value={partners.length}
                        icon={<i className="fa-solid fa-users-viewfinder"></i>}
                        color="#10b981"
                        subText="Assigned caretakers"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    dataSource={batches}
                    columns={batchColumns}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 1100 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title={editingBatch ? "Edit Livestock Batch" : "Register Livestock Batch"}
                open={isBatchModalVisible}
                onCancel={() => setIsBatchModalVisible(false)}
                footer={null}
            >
                <Form form={batchForm} layout="vertical" onFinish={handleSubmitBatch}>
                    <Form.Item label="Caretaker (Farmer)" required>
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name="partner_id" rules={[{ required: true }]} noStyle>
                                <Select showSearch optionFilterProp="children" style={{ width: '100%' }}>
                                    {partners.map(p => (
                                        <Option key={p.id} value={p.id}>{p.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Button type="default" icon={<PlusOutlined />} onClick={openCaretakerModal}>
                                Add
                            </Button>
                        </Space.Compact>
                    </Form.Item>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <Form.Item name="item_id" label="Livestock Item" rules={[{ required: true }]}>
                            <Select showSearch optionFilterProp="children" placeholder="Select livestock item">
                                {livestockItems.map(item => (
                                    <Option key={item.id} value={item.id}>{item.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="breed" label="Breed (Optional)">
                            <Input />
                        </Form.Item>
                    </div>
                    <Form.Item name="initial_head_count" label="Initial Head Count" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={1} />
                    </Form.Item>
                    <Form.Item name="acquisition_date" label="Acquisition Date" initialValue={dayjs()} rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>{editingBatch ? "Update Batch" : "Register Batch"}</Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`${editingEvent ? 'Edit' : 'Record'} Event for ${selectedBatch?.batch_number}`}
                open={isEventModalVisible}
                onCancel={closeEventModal}
                footer={null}
                width={520}
            >
                <Form form={eventForm} layout="vertical" onFinish={handleRecordEvent}>
                    <Form.Item name="event_type" label="Event Type" rules={[{ required: true }]}>
                        <Select disabled={!!editingEvent} onChange={(value) => {
                            setMortalityPhotos([]);
                            eventForm.setFieldValue('new_item_id', undefined);
                            eventForm.setFieldValue('head_count_change', value === 'BIRTH' ? 1 : 0);
                        }}>
                            <Option value="VACCINATION">Vaccination</Option>
                            <Option value="MEDICATION">Medication</Option>
                            <Option value="WEIGHT_CHECK">Weight Check</Option>
                            <Option value="BIRTH">Birth (Create Piglet Batch)</Option>
                            <Option value="MORTALITY">Mortality (- Headcount)</Option>
                            <Option value="STAGE_UPGRADE">Stage Upgrade (e.g. Piglets → Weaners)</Option>
                            <Option value="NOTE">General Note</Option>
                        </Select>
                    </Form.Item>

                    {/* Stage Upgrade Fields */}
                    {eventType === 'STAGE_UPGRADE' && (
                        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                            <Typography.Text strong style={{ display: 'block', marginBottom: 12, color: '#ea580c' }}>
                                Stage Upgrade Details
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                                Current stage: <strong>{selectedBatch?.current_stage_item?.name || selectedBatch?.item?.name || '—'}</strong>
                                &nbsp;({selectedBatch?.current_head_count} heads @ ₱{Number(selectedBatch?.unit_cost_per_head || 0).toLocaleString()}/head)
                            </Typography.Text>
                            <Row gutter={12}>
                                <Col span={14}>
                                    <Form.Item name="new_item_id" label="Upgrade To (New Stage Item)" rules={[{ required: eventType === 'STAGE_UPGRADE', message: 'Select new stage' }]}>
                                        <Select showSearch optionFilterProp="children" placeholder="Select new livestock stage" disabled={isDerivedEventEdit}>
                                            {livestockItems
                                                .filter(i => i.id !== (selectedBatch?.current_stage_item_id || selectedBatch?.item_id))
                                                .map(i => <Option key={i.id} value={i.id}>{i.name}</Option>)
                                            }
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={10}>
                                    <Form.Item name="new_unit_cost_per_head" label="New Cost/Head (₱)">
                                        <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" disabled={isDerivedEventEdit} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {eventType === 'BIRTH' && (
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                            <Typography.Text strong style={{ display: 'block', marginBottom: 12, color: '#15803d' }}>
                                Birth Details
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                                A new piglet batch will be created automatically. The mother batch head count will stay unchanged.
                            </Typography.Text>
                            <Form.Item
                                name="new_item_id"
                                label="Piglet Item"
                                rules={[{ required: eventType === 'BIRTH', message: 'Select the piglet livestock item' }]}
                            >
                                <Select showSearch optionFilterProp="children" placeholder="Select piglet item" disabled={isDerivedEventEdit}>
                                    {livestockItems.map(i => <Option key={i.id} value={i.id}>{i.name}</Option>)}
                                </Select>
                            </Form.Item>
                        </div>
                    )}

                    <Form.Item name="event_date" label="Date" initialValue={dayjs()} rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    {eventType !== 'STAGE_UPGRADE' && (
                        <Form.Item
                            name="head_count_change"
                            label={eventType === 'BIRTH' ? 'Piglets Born' : 'Head Count Change'}
                            initialValue={0}
                            help={eventType === 'BIRTH' ? 'Enter the number of live piglets to add as a new batch.' : 'Use negative for mortality, positive for adjustments.'}
                            rules={eventType === 'BIRTH'
                                ? [{ required: true, type: 'number', min: 1, message: 'Piglets born must be at least 1' }]
                                : []
                            }
                        >
                            <InputNumber style={{ width: '100%' }} min={eventType === 'BIRTH' ? 1 : undefined} disabled={isDerivedEventEdit} />
                        </Form.Item>
                    )}
                    <Form.Item name="cost_incurred" label="Cost Incurred (Optional)">
                        <InputNumber style={{ width: '100%' }} min={0} prefix="₱" />
                    </Form.Item>
                    <Form.Item name="notes" label="Details / Notes">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    {eventType === 'MORTALITY' && (
                        <Form.Item
                            label={
                                <Space>
                                    <CameraOutlined style={{ color: '#ef4444' }} />
                                    <span>Photo Documentation</span>
                                    <Tag color="red" style={{ marginLeft: 0 }}>Required for mortality</Tag>
                                </Space>
                            }
                        >
                            <Upload
                                listType="picture-card"
                                fileList={mortalityPhotos}
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                maxCount={MAX_MORTALITY_PHOTOS}
                                beforeUpload={(file) => {
                                    const isAcceptedType = ACCEPTED_MORTALITY_PHOTO_TYPES.includes(file.type);
                                    if (!isAcceptedType) {
                                        message.error('Only JPG, PNG, or WEBP photos are accepted.');
                                        return Upload.LIST_IGNORE;
                                    }

                                    const isWithinSizeLimit = file.size / 1024 / 1024 <= MAX_MORTALITY_PHOTO_SIZE_MB;
                                    if (!isWithinSizeLimit) {
                                        message.error(`Each photo must be ${MAX_MORTALITY_PHOTO_SIZE_MB} MB or smaller.`);
                                        return Upload.LIST_IGNORE;
                                    }

                                    return false;
                                }}
                                onChange={({ fileList }) => setMortalityPhotos(fileList.slice(0, MAX_MORTALITY_PHOTOS))}
                            >
                                {mortalityPhotos.length < MAX_MORTALITY_PHOTOS && (
                                    <div>
                                        <UploadOutlined />
                                        <div style={{ marginTop: 8, fontSize: 12 }}>Upload Photo</div>
                                    </div>
                                )}
                            </Upload>
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                Upload photos as evidence (max 5 photos, 5 MB each). Accepted: JPG, PNG, WEBP.
                            </Typography.Text>
                        </Form.Item>
                    )}

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block style={{ height: 44 }}>
                            {editingEvent ? 'Save Event Changes' : 'Record Event'}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Record Sale - ${selectedBatch?.batch_number}`}
                open={isSaleModalVisible}
                onCancel={() => setIsSaleModalVisible(false)}
                footer={null}
            >
                <Form form={saleForm} layout="vertical" onFinish={handleRecordSale}>
                    <Form.Item name="sale_date" label="Sale Date" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="head_count_sold" label="Head Count Sold" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={1} />
                    </Form.Item>
                    <Form.Item name="sale_amount" label="Total Sale Amount" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={0.01} />
                    </Form.Item>
                    <Form.Item name="partner_id" label="Customer">
                        <Select showSearch optionFilterProp="children" placeholder="Select customer">
                            {customers.map(c => (
                                <Option key={c.id} value={c.id}>{c.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="branch_id" label="Branch" rules={[{ required: true }]}>
                        <Select placeholder="Select branch">
                            {branches.map(b => (
                                <Option key={b.id} value={b.id}>{b.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="reference_number" label="Reference #">
                        <Input />
                    </Form.Item>
                    <Form.Item name="remarks" label="Remarks">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>Record Sale</Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Add Caretaker"
                open={isCaretakerModalVisible}
                onCancel={() => setIsCaretakerModalVisible(false)}
                footer={null}
                width={520}
            >
                <Form form={caretakerForm} layout="vertical" onFinish={handleCreateCaretaker}>
                    <Form.Item name="code" label="Caretaker Code" rules={[{ required: true, message: 'Code is required' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="name" label="Full Name" rules={[{ required: true, message: 'Name is required' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="phone" label="Phone Number">
                        <Input />
                    </Form.Item>
                    <Form.Item name="email" label="Email Address" rules={[{ type: 'email' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="address" label="Address">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            Create Caretaker
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Event History - ${selectedBatch?.batch_number}`}
                open={isHistoryModalVisible}
                onCancel={() => setIsHistoryModalVisible(false)}
                footer={null}
                width={600}
            >
                <Tabs
                    defaultActiveKey="events"
                    items={[
                        {
                            key: 'events',
                            label: 'Events',
                            children: (
                                <List
                                    itemLayout="horizontal"
                                    dataSource={batchEvents}
                                    renderItem={(item) => (
                                        <List.Item
                                            actions={[
                                                <Button
                                                    key="edit"
                                                    size="small"
                                                    type="text"
                                                    title="Edit event"
                                                    icon={<EditOutlined />}
                                                    onClick={() => openEditEventModal(item)}
                                                />
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    <Tag color={
                                                        item.event_type === 'MORTALITY' ? 'red' :
                                                        item.event_type === 'STAGE_UPGRADE' ? 'orange' :
                                                        item.event_type === 'BIRTH' ? 'green' : 'blue'
                                                    }>{item.event_type}</Tag>
                                                }
                                                title={`${dayjs(item.event_date).format('MMM D, YYYY')} - ${item.notes || 'No description'}`}
                                                description={
                                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                        {item.event_type === 'STAGE_UPGRADE' && (
                                                            <span>Upgraded to: <strong>{item.new_item?.name || `Item #${item.new_item_id}`}</strong>{item.new_unit_cost_per_head ? ` @ ₱${Number(item.new_unit_cost_per_head).toLocaleString()}/head` : ''}</span>
                                                        )}
                                                        {item.event_type === 'BIRTH' && (
                                                            <span>Piglets Born: <strong>{item.head_count_change}</strong>{item.birthed_batch ? ` - Batch ${item.birthed_batch.batch_number}` : ''}</span>
                                                        )}
                                                        {item.event_type !== 'STAGE_UPGRADE' && item.event_type !== 'BIRTH' && (
                                                            <span>Head Count Change: {item.head_count_change > 0 ? `+${item.head_count_change}` : item.head_count_change}</span>
                                                        )}
                                                        {item.cost_incurred > 0 && <span>Cost: ₱{Number(item.cost_incurred).toLocaleString()}</span>}
                                                        {item.event_type === 'MORTALITY' && item.photo_paths?.length > 0 && (
                                                            <div style={{ marginTop: 8 }}>
                                                                <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                                                                    <CameraOutlined /> Photo Documentation ({item.photo_paths.length})
                                                                </Typography.Text>
                                                                <Image.PreviewGroup>
                                                                    <Space wrap size={8}>
                                                                        {item.photo_paths.map((path, idx) => (
                                                                            <Image
                                                                                key={idx}
                                                                                src={`${storageBaseUrl}/storage/${path}`}
                                                                                width={72}
                                                                                height={72}
                                                                                style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #fee2e2' }}
                                                                                alt={`Mortality photo ${idx + 1}`}
                                                                            />
                                                                        ))}
                                                                    </Space>
                                                                </Image.PreviewGroup>
                                                            </div>
                                                        )}
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )
                        },
                        {
                            key: 'sales',
                            label: 'Sales',
                            children: (
                                <List
                                    itemLayout="horizontal"
                                    dataSource={batchSales}
                                    locale={{ emptyText: 'No sales recorded.' }}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <List.Item.Meta
                                                avatar={(
                                                    <Tag color={item.source === 'SALES_ORDER' ? 'blue' : 'green'}>
                                                        {item.source === 'SALES_ORDER' ? 'SALES ORDER' : 'SALE'}
                                                    </Tag>
                                                )}
                                                title={`${dayjs(item.sale_date).format('MMM D, YYYY')} - ${item.reference_number || 'No ref'}`}
                                                description={
                                                    <Space direction="vertical" size={0}>
                                                        <span>Heads Sold: {item.head_count_sold}</span>
                                                        <span>Amount: ₱{Number(item.sale_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                                        <span>Customer: {item.customer?.name || 'Walk-in'}</span>
                                                        {item.order_number && <span>Order: {item.order_number}</span>}
                                                        {item.remarks && <span>Remarks: {item.remarks}</span>}
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )
                        }
                    ]}
                />
            </Modal>
        </div>
    );
};

export default LivestockPage;
